"use client";

import React, { useState } from 'react';

interface Indicator {
  name: string;
  pillar: string;
  unit: string;
  latestValue: string;
  target2047: string;
  source: string;
  lastUpdated: string;
  frequency: string;
}

const indicators: Indicator[] = [
  { name: 'State GDP (GSDP)', pillar: 'Economy', unit: 'USD Billion', latestValue: '260', target2047: '3,000', source: 'RBI', lastUpdated: 'Q3 2025', frequency: 'Annual' },
  { name: 'GSDP Growth Rate', pillar: 'Economy', unit: '% YoY', latestValue: '8.2%', target2047: '12–14%', source: 'MoSPI', lastUpdated: 'Q3 2025', frequency: 'Annual' },
  { name: 'Per Capita Income', pillar: 'Economy', unit: 'USD/year', latestValue: '2,040', target2047: '24,000', source: 'RBI', lastUpdated: 'Q3 2025', frequency: 'Annual' },
  { name: 'Total Exports', pillar: 'Economy', unit: 'USD Billion', latestValue: '22.4', target2047: '300', source: 'DGFT', lastUpdated: 'Q2 2025', frequency: 'Quarterly' },
  { name: 'Formal Employment', pillar: 'Economy', unit: 'Million workers', latestValue: '4.2', target2047: '14', source: 'CMIE', lastUpdated: 'Q3 2025', frequency: 'Quarterly' },
  { name: 'FDI Inflows', pillar: 'Economy', unit: 'USD Billion/year', latestValue: '4.1', target2047: '50', source: 'RBI', lastUpdated: 'Q2 2025', frequency: 'Quarterly' },
  { name: 'Registered Startups', pillar: 'Innovation', unit: 'Count', latestValue: '1,847', target2047: '10,000', source: 'DPIIT', lastUpdated: 'Dec 2025', frequency: 'Annual' },
  { name: 'R&D Expenditure', pillar: 'Innovation', unit: '% of GSDP', latestValue: '0.8%', target2047: '3%', source: 'DST', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Patent Filings', pillar: 'Innovation', unit: 'Applications/year', latestValue: '2,340', target2047: '15,000', source: 'IPO', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Manufacturing GSDP Share', pillar: 'Manufacturing', unit: '% of GSDP', latestValue: '12.4%', target2047: '25%', source: 'GoTS', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Industrial Clusters', pillar: 'Manufacturing', unit: 'Count', latestValue: '47', target2047: '120', source: 'TSIIC', lastUpdated: 'Dec 2025', frequency: 'Annual' },
  { name: 'Farm Income per Acre', pillar: 'Agriculture', unit: 'INR/acre/year', latestValue: '32,000', target2047: '1,00,000', source: 'GoTS Agri', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Net Irrigated Area', pillar: 'Agriculture', unit: 'Million hectares', latestValue: '2.8', target2047: '5.5', source: 'CADA', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Installed RE Capacity', pillar: 'Energy', unit: 'GW', latestValue: '11.2', target2047: '30', source: 'MNRE', lastUpdated: 'Jan 2026', frequency: 'Quarterly' },
  { name: 'Rural Electrification', pillar: 'Energy', unit: '% households', latestValue: '99.2%', target2047: '100%', source: 'TSECL', lastUpdated: '2025', frequency: 'Annual' },
  { name: 'Road Network Length', pillar: 'Infrastructure', unit: '000 km', latestValue: '67', target2047: '120', source: 'MoRTH', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Tier-2 City Connectivity', pillar: 'Infrastructure', unit: '% complete', latestValue: '34%', target2047: '100%', source: 'GoTS', lastUpdated: 'Q3 2025', frequency: 'Semi-annual' },
  { name: 'Literacy Rate', pillar: 'Education', unit: '%', latestValue: '78.1%', target2047: '98%', source: 'Census / ASER', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Gross Enrolment Ratio (HE)', pillar: 'Education', unit: '%', latestValue: '32%', target2047: '60%', source: 'AISHE', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Hospital Bed Density', pillar: 'Healthcare', unit: 'Beds per 1,000', latestValue: '1.8', target2047: '5', source: 'NHM', lastUpdated: '2024', frequency: 'Annual' },
  { name: 'Forest Cover', pillar: 'Environment', unit: '% of state area', latestValue: '24.0%', target2047: '33%', source: 'FSI', lastUpdated: '2023', frequency: 'Biennial' },
  { name: 'Governance Efficiency Index', pillar: 'Governance', unit: 'Score /100', latestValue: '62', target2047: '95', source: 'PRS / GoTS', lastUpdated: '2025', frequency: 'Annual' },
];

const pillarColors: Record<string, string> = {
  Economy: 'text-blue-700 bg-blue-50 border-blue-100',
  Innovation: 'text-purple-700 bg-purple-50 border-purple-100',
  Manufacturing: 'text-orange-700 bg-orange-50 border-orange-100',
  Agriculture: 'text-green-700 bg-green-50 border-green-100',
  Energy: 'text-yellow-700 bg-yellow-50 border-yellow-100',
  Infrastructure: 'text-cyan-700 bg-cyan-50 border-cyan-100',
  Education: 'text-indigo-700 bg-indigo-50 border-indigo-100',
  Healthcare: 'text-rose-700 bg-rose-50 border-rose-100',
  Environment: 'text-emerald-700 bg-emerald-50 border-emerald-100',
  Governance: 'text-slate-700 bg-slate-50 border-slate-100',
};

const allPillars = ['All', ...Array.from(new Set(indicators.map(i => i.pillar)))];

export default function DataLibraryContent() {
  const [selectedPillar, setSelectedPillar] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = indicators.filter((ind) => {
    const matchPillar = selectedPillar === 'All' || ind.pillar === selectedPillar;
    const matchSearch = ind.name.toLowerCase().includes(search.toLowerCase()) ||
      ind.pillar.toLowerCase().includes(search.toLowerCase());
    return matchPillar && matchSearch;
  });

  return (
    <section className="section-padding bg-research">
      <div className="container-research">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search indicators..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-border rounded px-3 py-2 text-sm bg-white text-foreground placeholder:text-muted focus:outline-none focus:border-accent w-full sm:w-72"
          />
          <div className="flex flex-wrap gap-2">
            {allPillars.map((p) => (
              <button
                key={p}
                onClick={() => setSelectedPillar(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                  selectedPillar === p
                    ? 'bg-primary text-white border-primary' :'bg-white text-foreground border-border hover:border-accent'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted font-mono mb-4">
          Showing {filtered.length} of {indicators.length} indicators
        </p>

        {/* Table */}
        <div className="bg-white border border-border rounded shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="research-table">
              <thead>
                <tr>
                  <th>Indicator Name</th>
                  <th>Pillar</th>
                  <th>Unit</th>
                  <th>Latest Value</th>
                  <th>2047 Target</th>
                  <th>Source</th>
                  <th>Updated</th>
                  <th>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ind) => (
                  <tr key={ind.name}>
                    <td className="font-medium text-primary">{ind.name}</td>
                    <td>
                      <span className={`pillar-badge border ${pillarColors[ind.pillar] || 'text-muted bg-research border-border'}`}>
                        {ind.pillar}
                      </span>
                    </td>
                    <td className="text-muted text-xs">{ind.unit}</td>
                    <td className="font-mono font-semibold text-primary">{ind.latestValue}</td>
                    <td className="font-mono text-muted">{ind.target2047}</td>
                    <td>
                      <span className="source-tag">{ind.source}</span>
                    </td>
                    <td className="font-mono text-xs text-muted">{ind.lastUpdated}</td>
                    <td className="text-xs text-muted">{ind.frequency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Coming Soon Section */}
        <div className="mt-12 border border-dashed border-accent rounded p-8 bg-accent-light">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white border border-accent rounded flex items-center justify-center text-accent shrink-0">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 4v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-primary mb-1">
                Data Infrastructure — Coming Soon
              </h3>
              <p className="text-sm text-muted leading-relaxed max-w-2xl">
                TG2047 will gradually integrate structured datasets, annual budget data, and
                policy indicators into a queryable API. Planned capabilities include:
              </p>
              <ul className="mt-3 space-y-1">
                {[
                  'Downloadable CSV/JSON exports for all indicators',
                  'Annual budget allocation vs. actual expenditure tracking',
                  'Policy document cross-referencing',
                  'Historical time-series data (2015–2047)',
                  'Open API access for researchers',
                ].map((item) => (
                  <li key={item} className="text-sm text-foreground flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-accent inline-block shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted font-mono mt-3">
                Estimated availability: Q4 2026 · Contact: data@tg2047.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}